import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import { logger } from 'utils/logger';

import {
  createServicingDrawRequestAction,
  updateServicingDrawRequestAction,
} from 'store/loanApplications/actions';

import { DRAW_REQUEST_STATUS_OPTIONS, DrawRequestStatusEnum } from '../../Servicing.constants';
import { warningBoxStyles } from '../../Servicing.styles';
import { buildDrawRequestPayload, formatCurrency } from '../../Servicing.utils';
import { DrawRequestsTestIds } from '../DrawRequests.testIds';

import { drawRequestSchema, OBSERVABLE_FIELDS } from './DrawRequestDrawerContent.constants';
import { formFieldsStyles, infoBoxStyles } from './DrawRequestDrawerContent.styles';
import { getInitialFormValues } from './DrawRequestDrawerContent.utils';

const DrawRequestDrawerContent = ({
  setIsFormChanged,
  isSaving,
  setIsLoading,
  additionalProps: { drawRequest, applicationId, onSuccess, availableCredit },
  setOnSave,
  setIsSavingAllowed,
}) => {
  const dispatch = useDispatch();
  const isEditing = !!drawRequest;

  const initialFormValues = useMemo(() => getInitialFormValues(drawRequest), [drawRequest]);

  const {
    form,
    handleChange,
    isFormChanged,
    setInForm,
    setInitialFormValues,
    setForm,
    setSchema,
    validateForm,
  } = useForm(initialFormValues, {}, OBSERVABLE_FIELDS);

  useEffect(() => {
    setSchema(drawRequestSchema);
  }, [setSchema]);

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setInitialFormValues, setForm]);

  const onStatusChange = useCallback(
    (_, selectedOption) => {
      setInForm('status', selectedOption);
    },
    [setInForm],
  );

  const onSave = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      if (isEditing) {
        await dispatch(
          updateServicingDrawRequestAction({
            applicationId,
            drawRequestId: drawRequest.id,
            ...buildDrawRequestPayload(form),
            approvedAmount: form.approvedAmount ? Number(form.approvedAmount) : null,
            status: form.status?.value ?? DrawRequestStatusEnum.PENDING,
          }),
        );
      }

      if (!isEditing) {
        await dispatch(
          createServicingDrawRequestAction({
            applicationId,
            ...buildDrawRequestPayload(form),
          }),
        );
      }

      onSuccess();
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    form,
    isEditing,
    dispatch,
    applicationId,
    drawRequest,
    onSuccess,
    setIsLoading,
    validateForm,
  ]);

  const requestedAmount = Number(form.requestedAmount) || 0;
  const isExceedingCredit =
    !isEditing && !_.isNil(availableCredit) && requestedAmount > availableCredit;

  useEffect(() => {
    setIsSavingAllowed(!isExceedingCredit);
  }, [isExceedingCredit, setIsSavingAllowed]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  return (
    <Box sx={formFieldsStyles}>
      {!_.isNil(availableCredit) && (
        <Box sx={infoBoxStyles}>
          <Typography color="text.secondary" variant="regular14">
            Available Credit: {formatCurrency(availableCredit)}
          </Typography>
        </Box>
      )}

      <TextFieldCDS
        required
        dataTestId={DrawRequestsTestIds.REQUESTED_AMOUNT_INPUT}
        disabled={isSaving}
        label="Requested Amount"
        name="requestedAmount"
        value={form.requestedAmount}
        onChange={handleChange}
      />

      {isExceedingCredit && (
        <Box sx={warningBoxStyles}>
          <Typography color="warning.main" variant="regular14">
            Requested amount exceeds available credit of {formatCurrency(availableCredit)}
          </Typography>
        </Box>
      )}

      {isEditing && (
        <>
          <TextFieldCDS
            dataTestId={DrawRequestsTestIds.APPROVED_AMOUNT_INPUT}
            disabled={isSaving}
            label="Approved Amount"
            name="approvedAmount"
            value={form.approvedAmount}
            onChange={handleChange}
          />

          <AutocompleteCDS
            dataTestId={DrawRequestsTestIds.STATUS_SELECT}
            label="Status"
            name="status"
            optionKey="value"
            optionName="name"
            options={DRAW_REQUEST_STATUS_OPTIONS}
            value={form.status}
            onChange={onStatusChange}
          />
        </>
      )}

      <TextFieldCDS
        multiline
        dataTestId={DrawRequestsTestIds.NOTES_INPUT}
        disabled={isSaving}
        label="Notes"
        name="notes"
        rows={3}
        value={form.notes}
        onChange={handleChange}
      />
    </Box>
  );
};

DrawRequestDrawerContent.propTypes = {
  additionalProps: PropTypes.shape({
    applicationId: PropTypes.string.isRequired,
    onSuccess: PropTypes.func.isRequired,
    availableCredit: PropTypes.number,
    drawRequest: PropTypes.object,
  }).isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setIsSavingAllowed: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default memo(DrawRequestDrawerContent);
